import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  fetchPartPaymentStatus,
  hidePartPaymentNudge
} from '@actions/checkout';
import deviceHelper from '@utils/deviceHelper';
import Dynamic from '@base/Dynamic/Dynamic';
import useMounted from '@hooks/useMounted';
import { useFlagSelector } from '@hooks/useLayoutOptions';
import { goalLevelOnboardingStatus } from '@constants/index';
import { Spacings } from '@styles/index';
import GloNudge from '../Header/Nudges/GloNudge';

const PartPaymentNudge = Dynamic(() => import('./PartPaymentNudges'));
const VerificationNudge = Dynamic(() =>
  import('../Header/Nudges/VerificationNudge')
);

const Wrapper = styled.div`
  position: absolute;
  right: 0px;
  top: 90px;
  border-radius: ${Spacings.SPACING_2B};
`;

const dndGloNudgeRouteMap = {
  '/goal/[goalSlug]/[goalUID]/preferences': true
};

const nudgeTypes = {
  PART_PAYMENT: 'PART_PAYMENT',
  VERIFICATION: 'VERIFICATION',
  GOAL_LEVEL_ONBOARDING: 'GOAL_LEVEL_ONBOARDING'
};

const PaymentNudge = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    route,
    query: { goalUID: routeGoalUid = '' }
  } = router;
  const isCheckoutUrl = useFlagSelector('isCheckoutUrl');
  const isLoginUrl = route === '/login';
  const selectedGoalUid = useSelector(
    ({ auth }) => auth.selectedGoal?.uid || ''
  );
  const goalUID = routeGoalUid || selectedGoalUid;
  const [partpaymentGoalUID, setGoalUID] = useState(goalUID);
  const partPaymentStatus = useSelector(
    ({ checkout }) => checkout.partPaymentStatus?.[partpaymentGoalUID]
  );
  const cashResponse = useSelector(({ checkout }) => checkout.cashResponse);
  const shouldShowPartPaymentNudge = useSelector(
    ({ checkout }) => checkout.showPartPaymentNudge
  );
  const isMounted = useMounted();
  const isMobileDevice = deviceHelper.isMobileDevice();

  const [showPartPaymentNudge, setShowPartPaymentNudge] = useState(false);
  const verificationNudgeData = useSelector(({ auth }) => auth.nudgeData);
  const userOnboardingStatus = useSelector(
    ({ auth }) => auth.accessControl?.user_onboarding_status || -1
  );

  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);
  const hideNudges = useFlagSelector('hideNudges');

  const displayNudges =
    isLoggedIn &&
    isMounted &&
    !isCheckoutUrl &&
    !hideNudges &&
    !(isMobileDevice && isLoginUrl);

  const [isCombo, setIsCombo] = useState(false);

  const [selectedNudgeType, setSelectedNudgeType] = useState('');

  useEffect(() => {
    if (
      (partPaymentStatus?.is_active_part_set ||
        partPaymentStatus?.show_refund_screen) &&
      shouldShowPartPaymentNudge
    ) {
      setShowPartPaymentNudge(true);
    } else if (partPaymentStatus?.clash_details) {
      dispatch(fetchPartPaymentStatus(partPaymentStatus?.clash_details.uid));
      setGoalUID(partPaymentStatus?.clash_details.uid);
      setIsCombo(partPaymentStatus?.clash_details.type === 'Bundle');
    } else {
      setShowPartPaymentNudge(false);
    }
  }, [dispatch, partPaymentStatus, shouldShowPartPaymentNudge, isLoggedIn]);

  useEffect(() => {
    if (cashResponse?.has_ongoing_cash_payment || cashResponse?.clash_details) {
      setShowPartPaymentNudge(shouldShowPartPaymentNudge);
      setSelectedNudgeType(nudgeTypes.PART_PAYMENT);
    } else {
      setShowPartPaymentNudge(!shouldShowPartPaymentNudge);
    }
  }, [cashResponse, shouldShowPartPaymentNudge]);

  const goalInfo = useSelector(
    ({ topologies }) => topologies.data[goalUID]
  ) || { uid: goalUID };
  const gloData = goalInfo.goalLevelOnboarding || {};

  const showGloNudge =
    gloData.isAvailable &&
    [
      goalLevelOnboardingStatus.NEW,
      goalLevelOnboardingStatus.INCOMPLETE
    ].includes(userOnboardingStatus) &&
    !gloData.hideNudge &&
    !dndGloNudgeRouteMap[route] &&
    !!routeGoalUid; // showing only in goal pages

  useEffect(() => {
    let nudgeType = '';
    if (showPartPaymentNudge) nudgeType = nudgeTypes.PART_PAYMENT;
    else if (verificationNudgeData) nudgeType = nudgeTypes.VERIFICATION;
    else if (showGloNudge) nudgeType = nudgeTypes.GOAL_LEVEL_ONBOARDING;
    setSelectedNudgeType(nudgeType);
  }, [
    selectedNudgeType,
    showGloNudge,
    showPartPaymentNudge,
    verificationNudgeData
  ]);

  if (!displayNudges) return null;

  const closeNudge = () => {
    setSelectedNudgeType('');
  };

  const closePaymentNudge = () => {
    closeNudge();
    dispatch(hidePartPaymentNudge());
  };

  if (selectedNudgeType === nudgeTypes.PART_PAYMENT)
    return (
      <PartPaymentNudge
        closeNudge={closePaymentNudge}
        partPaymentStatus={partPaymentStatus}
        isCombo={isCombo}
        cashResponse={cashResponse}
      />
    );
  if (selectedNudgeType === nudgeTypes.VERIFICATION) {
    return (
      <Wrapper>
        <VerificationNudge
          nudgeData={verificationNudgeData}
          closeNudge={closeNudge}
        />
      </Wrapper>
    );
  }

  if (selectedNudgeType === nudgeTypes.GOAL_LEVEL_ONBOARDING) {
    return (
      <Wrapper>
        <GloNudge
          closeNudge={closeNudge}
          goalInfo={goalInfo}
          userOnboardingStatus={userOnboardingStatus}
        />
      </Wrapper>
    );
  }

  return null;
};

export default PaymentNudge;
